import React, { Component, createRef } from 'react';

import { IPhoneData, ISideMenu } from 'client/common/types';
import cn from 'utils/cn';
import debounce from 'utils/debounce';
import i18n from 'utils/i18n';

import RegionSwitcher, { IRegionSwitcherProps } from 'client/common/components/region-switcher';

import { ComponentRegistryConsumer } from '@bem-react/di';
// import { Transition } from 'react-transition-group';

import MenuItem from './menuItem';

import './index.css';

export const cnSidebar = cn('sidebar');
const TOP_OFFSET = 130;

enum SidebarPosition {
    default = 'default',
    fixedTop = 'fixedTop',
    fixedBottom = 'fixedBottom'
}

function fixedMod(position: SidebarPosition) {
    if (position === SidebarPosition.fixedTop) {
        return { fixed: 'top' };
    }
    if (position === SidebarPosition.fixedBottom) {
        return { fixed: 'bottom' };
    }

    return {};
}

export interface ISidebarProps extends IRegionSwitcherProps {
    isOpen: boolean;
    phoneData: IPhoneData;
    siteSearchUrl: string;
    menu: ISideMenu;
    menuIndexChain: number[];
    withScrollHandling?: boolean;
    pushMenuItem(item: number): void;
    popMenuItem(): void;
}

interface ISidebarState {
    viewportHeight: number;
    position: SidebarPosition;
    sidebarListMarginTop: number;
    sidebarListMarginBottom: number;
    hiddenContent: boolean;
    oldMenuIndexChain: number[] | null;
    direction: string;
}

class Sidebar extends Component<ISidebarProps, ISidebarState> {
    state = {
        viewportHeight: 0,
        position: SidebarPosition.default,
        sidebarListMarginTop: 0,
        sidebarListMarginBottom: 0,
        hiddenContent: false,
        oldMenuIndexChain: null,
        direction: ''
    };

    onResize = debounce(
        () => {
            this.setState({ viewportHeight: window.innerHeight });
        },
        300,
        true
    );
    private readonly listRef = createRef<HTMLDivElement>();
    private readonly listWrapperRef = createRef<HTMLDivElement>();
    private prevPosition: number = 0;

    componentDidMount() {
        if (this.props.withScrollHandling) {
            window.addEventListener('scroll', this.onScroll);
        }

        window.addEventListener('resize', this.onResize);

        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ viewportHeight: window.innerHeight }, this.updateListWrap);

        this.prevPosition = window.pageYOffset;
    }

    componentWillUnmount() {
        if (this.props.withScrollHandling) {
            window.removeEventListener('scroll', this.onScroll);
        }

        window.removeEventListener('resize', this.onResize);
    }

    onScroll = () => {
        this.updateListWrap();
        this.prevPosition = window.pageYOffset;
    }

    updateListWrap = () => {
        if (this.isSmallSidebar()) {
            this.updateSmallListWrap();
        } else {
            this.updateBiggerListWrap();
        }
    }

    /**
     * Проверка, что все пункты меню видны на одном экране.
     * @private
     * @returns {Boolean}
     */
    isSmallSidebar = () => {
        if (!this.listWrapperRef.current) {
            return false;
        }

        const { viewportHeight } = this.state;
        const { clientHeight } = this.listWrapperRef.current;
        const paddingTop = this.getListWrapperPadding();

        return clientHeight - paddingTop + TOP_OFFSET <= viewportHeight;
    }

    updateSmallListWrap = () => {
        if (this.state.position !== SidebarPosition.fixedTop) {
            this.setState({ position: SidebarPosition.fixedTop });

            this.setListWrapperPadding(TOP_OFFSET);
        }

        // FIXME: Подумать как сделать лучше (например унести в Redux из шапки)
        const header = document.querySelector<HTMLElement>('.header')!;
        const margin = parseInt(header.style.marginTop || '0', 10);

        this.setListMargin(margin);
    }

    updateBiggerListWrap = () => {
        const direction = this.prevPosition <= window.pageYOffset;

        if (direction) {
            this.handleDownDirection();
        } else {
            this.handleUpDirection();
        }
    }

    setListMargin = (margin: number) => {
        if (!this.isSmallSidebar()) {
            return;
        }

        this.setState({
            sidebarListMarginTop: margin,
            sidebarListMarginBottom: -margin
        });
    }

    // eslint-disable-next-line complexity
    handleDownDirection = () => {
        if (this.state.position === SidebarPosition.fixedTop && window.pageYOffset > 0) {
            this.setState({ position: SidebarPosition.default });
            this.setListWrapperPadding(window.pageYOffset + TOP_OFFSET);

            return;
        }

        const listWrapper = this.listWrapperRef.current;

        if (!listWrapper) {
            return;
        }

        const listWrapperHeight = listWrapper.clientHeight;
        const maxHeight = window.pageYOffset + this.state.viewportHeight;

        if (this.state.position !== SidebarPosition.fixedBottom && listWrapperHeight <= maxHeight) {
            this.setState({ position: SidebarPosition.fixedBottom });
            this.setListWrapperPadding(0);
        }
    }

    handleUpDirection = () => {
        const listWrapper = this.listWrapperRef.current;

        if (!listWrapper) {
            return;
        }

        if (this.state.position === SidebarPosition.fixedBottom) {
            const newPadding = (
                window.pageYOffset +
                this.state.viewportHeight -
                listWrapper.clientHeight
            );

            this.setState({ position: SidebarPosition.default });
            this.setListWrapperPadding(newPadding);
        }

        const offset = this.getListWrapperPadding();

        if (this.state.position !== SidebarPosition.fixedTop &&
            offset - TOP_OFFSET > window.pageYOffset
        ) {
            this.setState({ position: SidebarPosition.fixedTop });
            this.setListWrapperPadding(TOP_OFFSET);
        }
    }

    getListWrapperPadding() {
        if (!this.listWrapperRef.current) {
            return 0;
        }

        return parseInt(this.listWrapperRef.current.style.paddingTop || '0', 10);
    }

    // eslint-disable-next-line complexity, react/no-did-update-set-state
    componentDidUpdate(oldProps: ISidebarProps) {
        if (this.props.isOpen && !oldProps.isOpen) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ hiddenContent: false }, this.updateListWrap);
        }
        const notNull = Boolean(oldProps.menuIndexChain);

        // Нажали бэк у меню
        if (notNull && oldProps.menuIndexChain.length > this.props.menuIndexChain.length) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ oldMenuIndexChain: oldProps.menuIndexChain, direction: 'left' });
        }

        // Перешли в подраздел без перезагрузки страницы
        if (notNull && oldProps.menuIndexChain.length < this.props.menuIndexChain.length) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ oldMenuIndexChain: oldProps.menuIndexChain, direction: 'right' });
        }
    }

    setListWrapperPadding(padding: number) {
        if (!this.listWrapperRef.current) {
            return;
        }

        this.listWrapperRef.current.style.paddingTop = `${padding}px`;
    }

    // eslint-disable-next-line max-statements,complexity
    render() {
        const {
            isOpen,
            menuIndexChain,
            tld,
            regions,
            phoneData,
            siteSearchUrl,
            menu
        } = this.props;
        const {
            position,
            hiddenContent,
            oldMenuIndexChain,
            sidebarListMarginTop,
            sidebarListMarginBottom
            // direction
        } = this.state;
        const { phone, text } = phoneData;

        const needTempContent = Boolean(oldMenuIndexChain);
        let currentIndexChain = menuIndexChain;
        // let tempIndexChain: number[] = oldMenuIndexChain || [];

        const needPushNewIndecies = needTempContent && (
            (oldMenuIndexChain || []).length < menuIndexChain.length
        );

        if (needPushNewIndecies) {
            currentIndexChain = oldMenuIndexChain || [];
            // tempIndexChain = menuIndexChain;
        }

        const newMod = { active: isOpen ? 'yes' : 'no' };

        return (
            <ComponentRegistryConsumer id={'app'}>
                {({ Search }) => (
                    <div className={cnSidebar({ animated: 'yes' })}>
                        <div
                            className={cnSidebar(
                                'content',
                                { hidden: hiddenContent, ...fixedMod(position), ...newMod }
                            )}
                            >
                            <div className={cnSidebar('list-wrap')} ref={this.listWrapperRef}>
                                <div
                                    className={cnSidebar('list')}
                                    ref={this.listRef}
                                    style={{
                                        marginTop: sidebarListMarginTop,
                                        marginBottom: sidebarListMarginBottom
                                    }}
                                    >
                                    {this.renderMenu(menu, currentIndexChain)}
                                </div>
                                <div className={cnSidebar('footer')}>
                                    <div className={cnSidebar('footer-advertising')}>
                                        <Search siteSearchUrl={siteSearchUrl} type="sidebar" />
                                    </div>
                                    <div className={cnSidebar('footer-advertising')}>
                                        <div className={cnSidebar('footer-phone')}>
                                            {phone}
                                        </div>
                                        <div className={cnSidebar('phone-description')}>
                                            {text}
                                        </div>
                                    </div>
                                    <div className={cnSidebar('footer-location')}>
                                        <RegionSwitcher tld={tld} regions={regions} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
                }
            </ComponentRegistryConsumer>
        );
    }

    onItemClick = (index: number) => {
        this.props.pushMenuItem(index);
    }

    onBackClick = () => {
        this.props.popMenuItem();
    }

    renderMenu = (menu: ISideMenu, menuIndexChain: number[]) => {
        const curItems = this.getCurrentItems(menu, menuIndexChain);

        const items = curItems.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <MenuItem key={index} item={item} index={index} onItemClick={this.onItemClick} />
        ));

        if (menuIndexChain.length > 0) {
            const backwardItem = (
                <div className={cnSidebar('backward')} key="backward" onClick={this.onBackClick}>
                    <div className={cnSidebar('backward-arrow')} />
                    {i18n({
                        keyset: 'menu',
                        key: 'backward'
                    })}
                </div>
            );

            items.unshift(backwardItem);
        }

        return items;
    }

     getCurrentItems = (menu: ISideMenu, menuIndexChain: number[]) => {
         if (menuIndexChain.length === 0) {
             return menu.items;
         }
         // Показываем итемы предпоследнего активного итема
         let curItems = menu.items;

         for (let i = 0; i < menuIndexChain.length; i += 1) {
             const current = Number(menuIndexChain[i]);
             const currentItem = curItems[current];

             if (!currentItem.items) {
                 return curItems;
             }
             curItems = currentItem.items;
         }

         return curItems;
     }
}

export default Sidebar;
